import {
  slotAvailabilityEnumMapping,
  SlotAvailabilityType,
} from 'app/Bookings/constants'
import classNames from 'classnames'
import PageLoader from 'common/PageLoader'
import SlotGridSkeleton from 'common/Skeletons/SlotGridSkeleton'
import SlotGridButton from 'common/SlotGridButton'
import {
  dateFormats,
  SAD_PATH_SLOTS_ROW_LIMIT,
  SLOT_ROW_HEIGHT_PX,
  SLOTS_ROW_LIMIT_V3,
  TimeOfDay,
} from 'constants/common'
import formatFNS from 'date-fns/format'
import useThriveAuth from 'hooks/useThriveAuth'
import { useCallback, useLayoutEffect, useRef, useState } from 'react'
import styles from './index.module.scss'
import VerticalSlotGrid from './VerticalSlotGrid'

type SlotGridProps = {
  providerDetails: Provider
  dates: (Date | null)[]
  generatedSlots: DateSlot
  className?: string
  showMoreButton?: boolean
  showLimitedSlots?: boolean
  showSlotSelection?: boolean
  showSlotAvailabilityIcon?: boolean
  slotGridButtonClass?: string
  emptySlotClass?: string
  selectedSlot?: SelectedSlot
  onSlotClick?: (selectedSlot: SelectedSlot) => void
  appointmentMode?: SlotAvailabilityType
  isProviderPage?: boolean
  loadingSlots?: boolean
  loadingSlotNumber?: number
  emptySlots?: boolean
  emptySlotsComponent?: JSX.Element
  errorMessage?: string
  /** Caps the scroll container's visible height to this many rows (extra rows scroll into view) instead of hiding them. */
  maxVisibleRowLimit?: number
}

const SlotGrid: React.FC<SlotGridProps> = ({
  providerDetails,
  dates,
  generatedSlots,
  className = '',
  showMoreButton = false,
  showLimitedSlots = false,
  showSlotAvailabilityIcon = false,
  selectedSlot = null,
  slotGridButtonClass = '',
  emptySlotClass = '',
  onSlotClick,
  showSlotSelection = true,
  appointmentMode,
  isProviderPage = false,
  emptySlots = false,
  emptySlotsComponent,
  loadingSlots = false,
  loadingSlotNumber = 5,
  maxVisibleRowLimit,
}) => {
  //If we want show more functionality, then initialy, showLimitedSlots else show all slots
  const [showMore, setShowMore] = useState<boolean>(
    !showMoreButton && !showLimitedSlots
  )
  const [hideGradient, setHideGradient] = useState<boolean>(false)
  const [hasSlotsScrollOverflow, setHasSlotsScrollOverflow] = useState(false)
  const slotsContainerRef = useRef<HTMLDivElement>(null)
  const { isSupportOrThirdPartyUser, isAuthenticated } = useThriveAuth()
  const { id } = providerDetails

  const onCardClick = (slot: ProviderSlot) => {
    !!onSlotClick &&
      onSlotClick({
        slot,
        provider: providerDetails,
      })
  }

  const getCurrentDaySlots = (date: Date = new Date()) =>
    generatedSlots[formatFNS(date, dateFormats.YEAR_MONTH_DAY_FORMAT)]

  const isSlotValid = (slot: ProviderSlot) =>
    slot?.start && slot?.practiceId === providerDetails.practice?.id

  const getValidSlots = (slots: ProviderSlot[]) =>
    slots?.filter((slot) => isSlotValid(slot))

  const getMaxNumberOfSlots = (columnDates: (Date | null)[]) => {
    const realDates = columnDates.filter((d): d is Date => d !== null)

    if (!realDates.length) {
      return 0
    }

    let maxNoOfSlots = (getCurrentDaySlots(realDates[0]) || []).length

    realDates.forEach((date) => {
      if (getCurrentDaySlots(date)?.length > maxNoOfSlots) {
        maxNoOfSlots = getCurrentDaySlots(date).length
      }
    })

    return maxNoOfSlots
  }

  const hasValidSlotsInVisibleDates = dates.some(
    (date) =>
      date !== null && getValidSlots(getCurrentDaySlots(date))?.length > 0
  )

  const maxSlots = isProviderPage && getMaxNumberOfSlots(dates)

  const SLOT_ROW_LIMIT =
    isProviderPage && !hasValidSlotsInVisibleDates
      ? SAD_PATH_SLOTS_ROW_LIMIT
      : maxSlots || SLOTS_ROW_LIMIT_V3

  // Defaults to the sad-path row count so the existing scroll/fade behavior
  // (more slots than that scroll into view) is unchanged when this isn't set.
  const visibleRowCap = maxVisibleRowLimit ?? SAD_PATH_SLOTS_ROW_LIMIT

  const showProviderSlotScrollFade =
    isProviderPage && hasValidSlotsInVisibleDates && maxSlots > visibleRowCap

  const syncSlotsScrollOverflow = useCallback(() => {
    const el = slotsContainerRef.current

    if (!el || !showProviderSlotScrollFade) {
      setHasSlotsScrollOverflow(false)
      return
    }

    const hasOverflow = el.scrollHeight > el.clientHeight + 1
    setHasSlotsScrollOverflow(hasOverflow)

    if (hasOverflow) {
      const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1
      setHideGradient(atBottom)
    } else {
      setHideGradient(false)
    }
  }, [showProviderSlotScrollFade])

  useLayoutEffect(() => {
    if (!showProviderSlotScrollFade || loadingSlots) {
      setHasSlotsScrollOverflow(false)
      return
    }

    syncSlotsScrollOverflow()

    const el = slotsContainerRef.current

    if (!el || typeof ResizeObserver === 'undefined') {
      return
    }

    const observer = new ResizeObserver(syncSlotsScrollOverflow)
    observer.observe(el)

    return () => observer.disconnect()
  }, [
    showProviderSlotScrollFade,
    loadingSlots,
    syncSlotsScrollOverflow,
    generatedSlots,
    dates,
    maxSlots,
    showMore,
    isAuthenticated,
    isSupportOrThirdPartyUser,
  ])

  const showSlotsBottomFade =
    showProviderSlotScrollFade && hasSlotsScrollOverflow

  const handleScroll = useCallback(({ target }) => {
    // Check if the user has scrolled to the bottom
    if (target.scrollTop + target.clientHeight >= target.scrollHeight - 1) {
      setHideGradient(true)
    } else {
      setHideGradient(false)
    }
  }, [])

  return (
    <PageLoader
      withSkeleton
      isLoading={loadingSlots}
      isEmpty={emptySlots}
      Components={{
        Loading: <SlotGridSkeleton slots={loadingSlotNumber} />,
        Empty: emptySlotsComponent || null,
      }}
    >
      <div className={`${className} flex-fill`}>
        <div
          className={classNames({
            [styles.slots_scroll_host]: showSlotsBottomFade,
            [styles.slots_scroll_host_pass_through]: !showSlotsBottomFade,
            [styles.hide_gradient]: showSlotsBottomFade && hideGradient,
          })}
        >
          <div
            ref={slotsContainerRef}
            style={
              maxVisibleRowLimit
                ? { maxHeight: maxVisibleRowLimit * SLOT_ROW_HEIGHT_PX }
                : undefined
            }
            className={classNames('d-none d-lg-flex flex-nowrap', className, {
              [styles.slots_container]:
                isProviderPage &&
                hasValidSlotsInVisibleDates &&
                maxSlots > visibleRowCap,
              [styles.slots_container_authenticated]:
                isProviderPage &&
                isAuthenticated &&
                hasValidSlotsInVisibleDates &&
                maxSlots > visibleRowCap,
              [styles.slots_container_support]:
                isProviderPage &&
                isAuthenticated &&
                isSupportOrThirdPartyUser &&
                hasValidSlotsInVisibleDates &&
                maxSlots > visibleRowCap,
            })}
            data-testid={`${providerDetails.id}-slots`}
            onScroll={isProviderPage ? handleScroll : undefined}
          >
            <div className="row no-gutters flex-nowrap flex-fill w-100 m-0">
              {dates.map((date, colIndex) => {
                if (!date) {
                  return (
                    <div
                      key={`${id}-masked-col-${colIndex}`}
                      className={styles.day_slots_layout}
                    >
                      {Array.from({ length: SLOT_ROW_LIMIT }, (_, index) => (
                        <div
                          key={`card-${id}-mask-${colIndex}-${index}`}
                          className={classNames(
                            {
                              [styles.empty_slot_placeholder]: !isProviderPage,
                              [styles.empty_slot_placeholder_provider_page]: isProviderPage,
                            },
                            emptySlotClass,
                            'px-1',
                            {
                              [styles.empty_slot_placeholder_support_override]: isSupportOrThirdPartyUser,
                            }
                          )}
                        >
                          —
                        </div>
                      ))}
                    </div>
                  )
                }

                const currentDaySlots = getCurrentDaySlots(date)

                return (
                  <div
                    key={`${id}-${date.getDate()}-${date.getMonth()}`}
                    className={styles.day_slots_layout}
                  >
                    {currentDaySlots?.map((slot, index) => {
                      if (index < SLOT_ROW_LIMIT || showMore) {
                        if (isSlotValid(slot)) {
                          const isSlotSelected =
                            selectedSlot?.slot?.id === slot?.id
                          const slotAvailabilities = slot?.availabilityType?.map(
                            (availability) =>
                              slotAvailabilityEnumMapping[availability]
                          )
                          const isEveningSlot =
                            slot?.timeOfDay === TimeOfDay.EVENING
                          const isDaySlot = slot?.timeOfDay && !isEveningSlot

                          return (
                            <SlotGridButton
                              buttonVariant="outline-info"
                              key={`card-${id}-${index}`}
                              slotButtonClass={classNames(
                                {
                                  [styles.slot_button]: !isProviderPage,
                                  [styles.slot_button_provider_page]: isProviderPage,
                                },
                                slotGridButtonClass,
                                {
                                  [styles.slot_button_acted]:
                                    isSlotSelected && showSlotSelection,
                                  [styles.slot_button_support_override]: isSupportOrThirdPartyUser,
                                  [styles.slot_button_daySlot]: isDaySlot,
                                  [styles.slot_button_eveningSlot]: isEveningSlot,
                                }
                              )}
                              slot={slot}
                              onSlotClick={() => onCardClick(slot)}
                              showSlotAvailabilityIcon={
                                showSlotAvailabilityIcon
                              }
                              slotAvailabilities={
                                appointmentMode !== undefined
                                  ? [appointmentMode]
                                  : slotAvailabilities
                              }
                              isProviderPage
                            />
                          )
                        }

                        return (
                          <div
                            key={`card-${id}-${index}`}
                            className={classNames(
                              {
                                [styles.empty_slot_placeholder]: !isProviderPage,
                                [styles.empty_slot_placeholder_provider_page]: isProviderPage,
                              },
                              emptySlotClass,
                              'px-1',
                              {
                                [styles.empty_slot_placeholder_support_override]: isSupportOrThirdPartyUser,
                              }
                            )}
                          >
                            —
                          </div>
                        )
                      }
                    })}

                    {showMoreButton &&
                      !showMore &&
                      getValidSlots(currentDaySlots)?.length >
                        SLOTS_ROW_LIMIT_V3 && (
                        <button
                          type="button"
                          className={classNames(
                            'btn btn-link',
                            styles.more_btn
                          )}
                          onClick={() => setShowMore(true)}
                        >
                          more
                        </button>
                      )}
                  </div>
                )
              })}
            </div>
          </div>
          {showSlotsBottomFade && (
            <div className={styles.slots_bottom_fade} aria-hidden />
          )}
        </div>
        {/* Mobile responsive vertical view */}
        <div className="d-lg-none h-100">
          <VerticalSlotGrid
            providerDetails={providerDetails}
            dates={dates}
            generatedSlots={generatedSlots}
            showSlotSelection={showSlotSelection}
            selectedSlot={selectedSlot}
            onCardClick={onCardClick}
          />
        </div>
      </div>
    </PageLoader>
  )
}

export default SlotGrid
